import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import DataTable from '../components/DataTable';
import { fetchStates } from '../services/api';

const SearchBar = styled.div`
  margin-bottom: 1rem;
  
  input {
    width: 100%;
    max-width: 400px;
    padding: 0.75rem;
    border-radius: var(--border-radius);
    border: 1px solid var(--border-color, #444);
    font-size: 1rem;
    background-color: var(--bg-primary, #1a1a1a);
    color: var(--text-primary, #f0f0f0);
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 2px rgba(99, 91, 255, 0.2);
    }
    
    &::placeholder {
      color: var(--text-secondary, #aaa);
    }
  }
`;

// Notice component for view-only access
const AccessNotice = styled.div`
  background-color: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #f57c00;
  font-size: 0.9rem;
  
  svg {
    width: 20px;
    height: 20px;
    min-width: 20px;
  }
`;

function StatesPage() {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // User permission state
  const [currentUser, setCurrentUser] = useState(null);
  const [hasEditPermission, setHasEditPermission] = useState(false);
  
  useEffect(() => {
    const getStates = async () => {
      try {
        setLoading(true);
        const data = await fetchStates();
        setStates(data);
        setError(null);
      } catch (err) {
        setError('Failed to load states. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    getStates();
    
    // Get the current user from localStorage
    const userString = localStorage.getItem('user');
    if (userString) {
      try {
        const user = JSON.parse(userString);
        setCurrentUser(user);
        
        // Set permissions based on user role
        // Only superusers can edit/create/delete
        setHasEditPermission(user.is_superuser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);
  
  const filteredStates = states.filter(state => 
    state.state_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    state.state_abbreviation.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const columns = [
    {
      header: 'ID',
      accessor: 'state_id',
    },
    {
      header: 'State Name',
      accessor: 'state_name',
    },
    {
      header: 'Abbreviation',
      accessor: 'state_abbreviation',
    }
  ];
  
  return (
    <div>
      <PageHeader 
        title="States" 
        description="Browse all U.S. states and territories in the database."
      />
      
      {/* Display notice for view-only users */}
      {!hasEditPermission && (
        <AccessNotice>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C16.418 20 20 16.418 20 12C20 7.582 16.418 4 12 4C7.582 4 4 7.582 4 12C4 16.418 7.582 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z" fill="currentColor"/>
          </svg>
          <div>
            <strong>View-only access:</strong> You can browse and search states, but you don't have permission to add, edit, or delete.
          </div>
        </AccessNotice>
      )}
      
      <Card>
        <SearchBar>
          <input 
            type="text"
            placeholder="Search states..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </SearchBar>
        
        {loading ? (
          <p>Loading states...</p>
        ) : error ? (
          <p>{error}</p>
        ) : (
          <DataTable 
            columns={columns} 
            data={filteredStates}
            emptyMessage="No states found matching your search criteria."
          />
        )}
      </Card>
    </div>
  );
}

export default StatesPage;